import { type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { type ExitInParkingCardFormValues } from 'src/features/cards/components/ExitInParkingCardModalForm';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { useParking } from 'src/providers/ParkingProvider';
import { Checkbox } from 'src/registry/ui/Checkbox';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';

type ExitInParkingCardFormProps = {
    form: UseFormReturn<ExitInParkingCardFormValues>;
    onSubmit: (values: ExitInParkingCardFormValues) => void;
};

function ExitInParkingCardForm({ form, onSubmit }: ExitInParkingCardFormProps) {
    const { parkingId } = useParking();
    const { formatMessage } = useIntl();

    const { data: pointsData } = useListPoints({ parkingId, limit: 100 });

    const parkingPointOptions = pointsData?.results.map((point) => ({
        label: point.name,
        value: point.id,
    }));

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="open_point"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(cardMessages.openGateOnExit)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="exit_point_id"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(cardMessages.exitPoint)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={formatMessage(cardMessages.exitPointPlaceholder)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {parkingPointOptions?.map((parkingPointOption) => (
                                            <SelectItem key={parkingPointOption.value} value={parkingPointOption.value}>
                                                {parkingPointOption.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default ExitInParkingCardForm;
